import {
	Icon,
	FontIconName,
	Anchor,
	DateString,
	Space,
	Toolbar,
	Button,
	DropOver,
	ActionListItem,
	ActionList,
	ActionListBox
} from "client/Components";
import { LocationType, TicketType } from "client/Api/EventsManagement/eventsManagement";
import { localize } from "client_legacy/ndaw/culture";
import {
	PropsWithChildren,
	ReactElement,
	RefObject,
	useRef,
	useState,
	useCallback
} from "react";
import styles from "./EventDetails.module.less";
import {
	isNotNil,
	isNotNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import { EventAttendeeKind } from "client/Api/Hub/Events/eventViewer";
import { never } from "client_legacy/ndaw/errors";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { AttendOptions } from "./EventContent";
import { isValidDate } from "client/DateAndTime/dateTimeUtils";

export type EventDetailsInteractiveProps = ConditionalProps<
	{
		isAttendanceEnabled: boolean;
		attendeeKind: EventAttendeeKind | null;
		onAttend: (options: AttendOptions) => void;
		onAddToCalendar: () =>void;
	},
	"isInteractive"
>;

export const eventDetailsInvalidInput = Symbol();

export type EventDetailsInvalidInput = typeof eventDetailsInvalidInput;

export type EventDetailsProps = {
	startDate: Date | null;
	endDate: Date | null;
	signUpDeadline: Date | null;
	locationType: LocationType;
	location: string | null;
	externalUrl: string | null | EventDetailsInvalidInput;
	onlineEventUrl: string | null | EventDetailsInvalidInput;
	ticketType: TicketType | null;
	availableSpots: number | null | EventDetailsInvalidInput;
	totalAttendees: number;
	isBeforeSignUpDate: boolean;
	isBeforeEventStartDate: boolean;
	timezone: TimeZone;
}
	& EventDetailsInteractiveProps;

export const EventDetails = (props: EventDetailsProps): ReactElement | null => {
	const {
		startDate,
		endDate,
		signUpDeadline,
		locationType,
		location,
		externalUrl,
		onlineEventUrl,
		ticketType,
		availableSpots,
		totalAttendees,
		attendeeKind,
		isBeforeSignUpDate,
		isBeforeEventStartDate,
		onAttend,
		onAddToCalendar,
		timezone,
		isAttendanceEnabled,
		isInteractive
	} = props;

	const areSpotsAvailable: boolean = isValidInput(availableSpots)
		? isNotNil(availableSpots)
			? totalAttendees < availableSpots
			: true
		: false;

	const isAttending: boolean = isInteractive && isNotNil(attendeeKind);
	const isTheAttendanceButtonEnabled: boolean = (isAttending && isBeforeEventStartDate)
		|| (
			!isAttending
			&& isBeforeSignUpDate
			&& areSpotsAvailable
		);

	const canRenderLocation = (locationType === LocationType.Hybrid || locationType === LocationType.Physical)
		&& isNotNilOrWhiteSpace(location);

	const canRenderEventUrl = (locationType === LocationType.Hybrid || locationType === LocationType.Online)
		&& isNotNil(onlineEventUrl);

	const canRenderDateRange = isNotNil(startDate) && isNotNil(endDate);

	const canRenderAnyDetail: boolean = canRenderLocation
		|| canRenderEventUrl
		|| canRenderDateRange
		|| isNotNil(externalUrl)
		|| isNotNil(signUpDeadline)
		|| isNotNil(ticketType)
		|| isNotNil(availableSpots);

	const renderDate = (date: Date): ReactElement | string => {
		if (!isValidDate(date)) {
			return localize("_NoValidInput");
		}

		return (
			<DateString
				value={date}
				format="RelativeDayWithTime"
				timezone={timezone}
			/>
		);
	};

	const renderDetailItems = (): ReactElement => (
		<>
			{canRenderLocation && (
				<li>
					<IconAndText
						icon="location-dot"
						trackingId="location"
						href={convertToGoogleMapsSearchURL(location)}
					>
						{location}
					</IconAndText>
				</li>
			)}
			{canRenderEventUrl && (
				<li>
					<IconAndText
						icon="video"
						trackingId="video"
						href={isValidInput(onlineEventUrl)
							? onlineEventUrl
							: undefined
						}
					>
						{isValidInput(onlineEventUrl)
							? localize("_OnlineMeeting")
							: localize("_NoValidInput")
						}
					</IconAndText>
				</li>
			)}
			{canRenderDateRange && (
				<li>
					<IconAndText
						icon="calendar"
						trackingId="start-end-date"
					>
						{renderDate(startDate)}
						<Space />
						-
						<Space />
						{renderDate(endDate)}
					</IconAndText>
				</li>
			)}
			{externalUrl && (
				<li>
					<IconAndText
						icon="arrow-up-right-from-square"
						href={isValidInput(externalUrl)
							? externalUrl
							: undefined
						}
						trackingId="external-url"
					>
						{isValidInput(externalUrl)
							? externalUrl
							: localize("_NoValidInput")
						}
					</IconAndText>
				</li>
			)}
			{signUpDeadline && (
				<li>
					<IconAndText
						icon="check"
						trackingId="sign-up-deadline"
					>
						<div>{localize("_SignUpBefore")}:</div>
						{renderDate(signUpDeadline)}
					</IconAndText>
				</li>
			)}
			{isNotNil(ticketType) && (
				<li>
					<IconAndText
						icon="coin-blank"
						trackingId="ticket-type"
					>
						{ticketTypeToTextMap[ticketType]}
					</IconAndText>
				</li>
			)}
			{availableSpots && (
				<li>
					<IconAndText
						icon="user-check"
						trackingId="available-spots"
					>
						<div>{localize("_SpotsTaken")}:</div>
						{isValidInput(availableSpots)
							?`${totalAttendees} / ${availableSpots}`
							: localize("_NoValidInput")
						}
					</IconAndText>
				</li>
			)}
		</>
	);

	if (!isInteractive && !canRenderAnyDetail) {
		return null;
	}

	return (
		<div className={styles.root}>
			{canRenderAnyDetail && (
				<>
					<h4>{localize("_EventDetails")}</h4>
					<ul className={styles.list}>
						{renderDetailItems()}
					</ul>
				</>
			)}
			{isInteractive && (
				<EventButtons
					attendeeKind={attendeeKind}
					externalUrl={isValidInput(externalUrl)
						? externalUrl
						: null
					}
					isAttendanceEnabled={isAttendanceEnabled}
					isTheAttendanceButtonEnabled={isTheAttendanceButtonEnabled}
					locationType={locationType}
					onAddToCalendar={onAddToCalendar}
					onAttend={onAttend}
				/>
			)}
		</div>
	);
};
EventDetails.displayName="EventDetails";

export type IconAndTextProps = {
	icon: FontIconName;
	href?: string;
	trackingId: string;
};

export const IconAndText = (props: PropsWithChildren<IconAndTextProps>): ReactElement => {
	const {
		icon,
		href,
		trackingId,
		children
	} = props;

	const renderContent = (): ReactElement => (
		<div className={styles.itemContent}>
			{children}
		</div>
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.itemContainer}
		>
			<div className={styles.icon}>
				<Icon value={icon} />
			</div>
			{href
				? (
					<Anchor
						href={href}
						className={styles.link}
						target="_blank"
					>
						{renderContent()}
					</Anchor>
				)
				: renderContent()
			}
		</div>
	);
};
IconAndText.displayName="IconAndText";

export type EventButtonsProps = {
	attendeeKind: EventAttendeeKind | null;
	locationType: LocationType;
	isTheAttendanceButtonEnabled: boolean;
	isAttendanceEnabled: boolean;
	externalUrl: string | null;
	onAddToCalendar(): void;
	onAttend(options: AttendOptions): void;
};

export const EventButtons = (props: EventButtonsProps): ReactElement => {
	const {
		attendeeKind,
		locationType,
		isTheAttendanceButtonEnabled,
		isAttendanceEnabled,
		externalUrl,
		onAddToCalendar,
		onAttend
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);

	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const isAttending: boolean = isNotNil(attendeeKind);

	const goToEventHandler = useCallback(
		(externalUrl: string): void => {
			navigateTo(externalUrl, true);
		},
		[]
	);

	const renderAttendButton = (): ReactElement | null => {
		if (!isAttendanceEnabled) {
			if (externalUrl) {
				return (
					<Button
						trackingId="go-to-event"
						onClick={() => goToEventHandler(externalUrl)}
						text={localize("_GoToEvent")}
						kind="primary"
					/>
				);
			} else {
				return null;
			}
		}

		switch(locationType) {
			case LocationType.Hybrid:
				const text: string = isAttending
					? attendeeKind === EventAttendeeKind.Online
						? localize("_YouAreAttendingOnline")
						: localize("_YouAreAttendingPhysically")
					: localize("_Attend");

				return (
					<>
						<Button
							ref={ref}
							trackingId="attend"
							onClick={()=> setIsOpen(true)}
							text={text}
							kind={isAttending
								? "secondary"
								: "primary"
							}
							isDisabled={!isTheAttendanceButtonEnabled}
							rightIcon="chevron-down"
						/>
						{isOpen && ref.current && (
							<DropOver
								target={ref.current}
								onClose={() => setIsOpen(false)}
							>
								<ActionListBox
									menuTitle={text}
									trackingId="attend-event"
								>
									<ActionList>
										{(attendeeKind === EventAttendeeKind.Physical || !isAttending) && (
											<ActionListItem
												trackingId="attend-online"
												text={localize("_AttendOnline")}
												icon="video"
												onClick={
													() => onAttend({
														isAttending: true,
														kind: EventAttendeeKind.Online,
														wasAttending: isNotNil(attendeeKind)
													})
												}
												/>
											)}
										{(attendeeKind === EventAttendeeKind.Online || !isAttending) && (
											<ActionListItem
												trackingId="attend-physically"
												text={localize("_AttendPhysically")}
												icon="location-dot"
												onClick={
													() => onAttend({
														isAttending: true,
														kind: EventAttendeeKind.Physical,
														wasAttending: isNotNil(attendeeKind)
													})
												}
											/>
										)}
										{isAttending && (
											<ActionListItem
												trackingId="dont-attend"
												text={localize("_DontAttend")}
												icon="xmark"
												onClick={() => onAttend({isAttending: false})}
											/>
										)}
									</ActionList>
								</ActionListBox>
							</DropOver>
						)}
					</>
				);
			case LocationType.Online:
			case LocationType.Physical:
				return (
					<Button
						trackingId="attend"
						onClick={
							() => onAttend(!isAttending
								? {
									isAttending: true,
									kind: locationTypeToAttendeeKindMap[locationType],
									wasAttending: false
								}
								: { isAttending: false }
							)
						}
						text={isAttending ? localize("_DontAttend") : localize("_Attend")}
						kind={isAttending ? "secondary" : "primary"}
						isDisabled={!isTheAttendanceButtonEnabled}
					/>
				);
			default:
				never(locationType);
		}
	};

	const attendButtons: ReactElement | null = renderAttendButton();

	return (
		<div className={styles.buttons}>
			{attendButtons && (
				<div className={styles.attendButton}>
					{attendButtons}
				</div>
			)}
			<div className={styles.calendarButton}>
				<Toolbar>
					{(isAttending || !isAttendanceEnabled) && (
						<Toolbar.Button
							trackingId="add-to-calendar"
							onClick={onAddToCalendar}
							text={localize("_AddToCalendar")}
							kind="transparent"
							icon="plus"
						/>
					)}
				</Toolbar>
			</div>
		</div>
	);
};

EventButtons.displayName = "EventButtons";

const ticketTypeToTextMap : { [key in TicketType]: string | undefined } = {
	[TicketType.Free]: localize("_(Cost)Free"),
	[TicketType.Paid]: localize("_Paid")
};

const locationTypeToAttendeeKindMap : { [key in Exclude<LocationType, LocationType.Hybrid>]: EventAttendeeKind} = {
	[LocationType.Online]: EventAttendeeKind.Online,
	[LocationType.Physical]: EventAttendeeKind.Physical
};

const GOOGLE_MAPS_API: string = "https://www.google.com/maps/search/?api=1";

function convertToGoogleMapsSearchURL(
	address: string
): string {
	return `${GOOGLE_MAPS_API}&query=${encodeURIComponent(address)}`;
}

function isValidInput<T>(input: T | EventDetailsInvalidInput): input is T {
	return input !== eventDetailsInvalidInput;
}